import { useState, useCallback, useEffect } from "react";
import { rotatePdf, getPdfPageCount } from "./operations";
import { DownloadTrialHint } from "@/components/DownloadTrialHint";
import { FileDropZone, ProcessingState, DownloadButton } from "@/components/ToolLayout";
import { ToolWithSidebar } from "@/components/ToolWithSidebar";
import { PdfBeforeAfter } from "@/components/PdfBeforeAfter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { usePdfPageThumbnails, PdfThumbnailLoading } from "@/components/PdfPageThumbnails";
import { pdfBytesToBlob, loadPdfDocument } from "./utils";
import { detectSidewaysPages, batchTurns, type TurnSuggestion } from "./orientDetect";
import { BatchRunner, BatchModeToggle } from "@/components/BatchRunner";
import { stemOfPdfName } from "./pdfMetadata";
import { fmtDelta } from "./rotateLabel";
import { useRunToken } from "@/hooks/useRunToken";
import { plural } from "@/lib/plural";
import { formatFileSize } from "@/lib/formatFileSize";
import { Info, Minus, Plus, RotateCcw, RotateCw, ScanLine } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Fixes pages in a PDF that are sideways, upside down, or slightly skewed. Rotate every page 90 degrees at once, turn an individual page either way, or type an exact angle for a page that's just slightly crooked — with a real preview of each one.\n\nUpload the PDF and review the page previews. Use "Rotate all" to turn every page at once, the arrows under a page for quick 90-degree fixes, or type an angle (or use the − and + buttons) for fine correction. Apply the rotation, compare the original with the result, and download.`,
  tip: `Pages that have a text layer are checked on your device: if most of a page's text runs sideways or upside down, it is marked "Looks sideways" with the turn that would fix it. Nothing changes until you press the button for that page, or "Fix N pages". Scanned pages with no text layer are not checked (the tool cannot read them without text recognition), so look at those yourself. To turn several PDFs the same way, switch to Several files: all pages, odd pages or even pages, one ZIP.\n\nA 90-degree turn only changes how the page is displayed, so everything in your PDF — text, links, form fields, bookmarks, the title — stays exactly as it was. A custom angle redraws that page at its original sharpness, with its text still selectable; the page grows a little to hold the turned content, and links or form fields on that page are not kept.`,
};

/** Keeps an angle in -180..180 (a quarter turn to the left is -90, not 270). */
const normalise = (a: number) => {
  const n = ((((a + 180) % 360) + 360) % 360) - 180;
  return n === -180 ? 180 : Math.round(n * 1000) / 1000;
};

/**
 * One page preview, turned by `angle`, shrunk so the WHOLE turned page fits its tile (a sideways page
 * used to be clipped at the tile's edges). The tile is grey and the page has an outline, so a landscape
 * page does not pass for a portrait one with big margins.
 */
function RotatedThumb({ url, pageNumber, angle, aspect, onAspect }: { url: string; pageNumber: number; angle: number; aspect: number; onAspect: (a: number) => void }) {
  const rad = (angle * Math.PI) / 180;
  const c = Math.abs(Math.cos(rad));
  const s = Math.abs(Math.sin(rad));
  const TILE_ASPECT = 0.75; // width / height of the tile
  // The page is `aspect` wide and 1 tall; the tile is 1 wide and 1/TILE_ASPECT tall.
  const boxW = aspect * c + s;
  const boxH = aspect * s + c;
  const scale = Math.min(1 / boxW, 1 / TILE_ASPECT / boxH);
  return (
    <div className="relative w-full overflow-hidden rounded-lg border bg-muted" style={{ aspectRatio: String(TILE_ASPECT) }}>
      <img
        src={url}
        alt={`Page ${pageNumber}${angle ? `, turned ${angle}°` : ""}`}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalHeight > 0) onAspect(img.naturalWidth / img.naturalHeight);
        }}
        className="absolute left-1/2 top-1/2 bg-white shadow ring-1 ring-black/10"
        style={{ width: `${aspect * scale * 100}%`, height: "auto", transform: `translate(-50%, -50%) rotate(${angle}deg)` }}
      />
    </div>
  );
}

const TURN_LABEL = (t: number) => (t === 90 ? "90° right" : t === -90 ? "90° left" : "180°");

/** Several PDFs, the same turn for each, one ZIP. Never auto-detects: only what the user chose is changed. */
function BatchRotate() {
  const [turn, setTurn] = useState<90 | 180 | -90>(90);
  const [pages, setPages] = useState<"all" | "odd" | "even">("all");
  const choice = (active: boolean) => (active ? "default" : "outline") as "default" | "outline";
  return (
    <BatchRunner
      accept=".pdf" noun="PDF" zipName="rotated-pdfs.zip" runLabel="Rotate all" includeOriginals
      process={async (f) => {
        const count = await getPdfPageCount(f);
        if (!count) throw new Error(`${f.name} has no pages`);
        const bytes = await rotatePdf(f, batchTurns(count, turn, pages));
        return { blob: pdfBytesToBlob(bytes), fileName: `${stemOfPdfName(f.name)}-rotated.pdf` };
      }}
    >
      <div className="space-y-3 mb-3">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Turn">
          <span className="text-sm text-muted-foreground mr-1">Turn</span>
          {([90, -90, 180] as const).map((t) => (
            <Button key={t} size="sm" variant={choice(turn === t)} aria-pressed={turn === t} onClick={() => setTurn(t)}>{TURN_LABEL(t)}</Button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Pages">
          <span className="text-sm text-muted-foreground mr-1">Pages</span>
          {(["all", "odd", "even"] as const).map((p) => (
            <Button key={p} size="sm" variant={choice(pages === p)} aria-pressed={pages === p} onClick={() => setPages(p)}>{p === "all" ? "All pages" : p === "odd" ? "Odd pages" : "Even pages"}</Button>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Every PDF gets the same turn. Pages are not checked for sideways text in this mode, so only what you pick here changes.</p>
      </div>
    </BatchRunner>
  );
}

export default function RotatePdf() {
  const [batchMode, setBatchMode] = useState(false);
  return (
    <ToolWithSidebar toolId="rotate-pdf" categoryId="pdf-tools" whatItDoes={TOOL_INFO_CONTENT.what} proTip={TOOL_INFO_CONTENT.tip}>
      <BatchModeToggle batch={batchMode} onChange={setBatchMode} />
      {batchMode ? <BatchRotate /> : <RotateOne />}
    </ToolWithSidebar>
  );
}

function RotateOne() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [angles, setAngles] = useState<Record<number, number>>({});
  // What is typed in an angle box while it has focus, so "-0.4" or "1e" can be typed without being rewritten.
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const [aspects, setAspects] = useState<Record<number, number>>({});
  const [processing, setProcessing] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [result, setResult] = useState<Blob | null>(null);
  const [fileError, setFileError] = useState("");
  const { thumbnails, loading } = usePdfPageThumbnails(file);
  const runs = useRunToken();
  // Pages whose text runs sideways, found on the device from the text layer. Suggestions only; nothing is turned until the user accepts.
  const [suggestions, setSuggestions] = useState<Record<number, TurnSuggestion>>({});
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    setSuggestions({});
    if (!file) return;
    let live = true;
    setChecking(true);
    (async () => {
      try {
        const pdf = await loadPdfDocument(file);
        const found = await detectSidewaysPages(pdf, () => live);
        if (live) setSuggestions(Object.fromEntries(found.map((f) => [f.page, f])));
      } catch {
        /* no suggestions; the manual controls still work */
      } finally {
        if (live) setChecking(false);
      }
    })();
    return () => { live = false; };
  }, [file]);

  const stale = useCallback(() => {
    runs.cancel();
    setProcessing(false);
    setProgressMsg("");
    setResult(null);
  }, [runs]);

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0];
    if (!f) return;
    stale();
    setFileError("");
    try {
      const count = await getPdfPageCount(f);
      if (!count) throw new Error("no pages");
      // Every page starts at 0 straight away, so the buttons work while the previews are still loading.
      setAngles(Object.fromEntries(Array.from({ length: count }, (_, i) => [i + 1, 0])));
      setDrafts({});
      setAspects({});
      setPageCount(count);
      setFile(f);
    } catch {
      setFileError(`"${f.name}" couldn't be read as a PDF. It may not be a PDF at all, or it may be damaged. Nothing was used. Try another file.`);
    }
  }, [stale]);

  const reset = () => {
    stale();
    setFile(null);
    setPageCount(0);
    setAngles({});
    setDrafts({});
    setFileError("");
  };

  const turn = (pageNumber: number, delta: number) => {
    setAngles((prev) => ({ ...prev, [pageNumber]: normalise((prev[pageNumber] || 0) + delta) }));
    setDrafts((d) => { const n = { ...d }; delete n[pageNumber]; return n; });
    stale();
  };

  const turnAll = (delta: number) => {
    setAngles((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, normalise(v + delta)])));
    setDrafts({});
    stale();
  };

  const turnParity = (parity: "odd" | "even") => {
    setAngles((prev) => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, (Number(k) % 2 === 1) === (parity === "odd") ? normalise(v + 90) : v])));
    setDrafts({});
    stale();
  };

  /** The user accepts the suggested turn for these pages (the turn is added to whatever they already set). */
  const accept = (pages: number[]) => {
    setAngles((prev) => {
      const next = { ...prev };
      for (const p of pages) {
        const sug = suggestions[p];
        if (sug) next[p] = normalise(sug.turn);
      }
      return next;
    });
    setDrafts({});
    stale();
  };

  const typeAngle = (pageNumber: number, text: string) => {
    setDrafts((d) => ({ ...d, [pageNumber]: text }));
    const n = parseFloat(text);
    if (Number.isFinite(n)) {
      setAngles((prev) => ({ ...prev, [pageNumber]: normalise(n) }));
      stale();
    }
  };

  const finishTyping = (pageNumber: number) => {
    setDrafts((d) => { const n = { ...d }; delete n[pageNumber]; return n; });
  };

  const apply = async () => {
    if (!file) return;
    runs.cancel();
    setResult(null);
    const me = runs.start();
    setProcessing(true);
    setProgressMsg("");
    try {
      const bytes = await rotatePdf(file, angles, (m) => runs.isCurrent(me) && setProgressMsg(m), () => !runs.isCurrent(me));
      if (!runs.isCurrent(me)) return;
      setResult(pdfBytesToBlob(bytes));
      toast.success("Rotation applied");
    } catch (err) {
      if (!runs.isCurrent(me)) return;
      console.error("Rotate failed:", err);
      toast.error("Failed to rotate PDF");
    } finally {
      if (runs.isCurrent(me)) {
        setProcessing(false);
        setProgressMsg("");
      }
    }
  };

  const changedPages = Object.entries(angles).filter(([, v]) => v !== 0).map(([k]) => Number(k)).sort((a, b) => a - b);
  const pendingFixes = Object.values(suggestions).filter((sg) => (angles[sg.page] ?? 0) !== sg.turn).map((sg) => sg.page).sort((a, b) => a - b);
  const hasAnyRotation = changedPages.length > 0;
  const hasCustomAngle = Object.values(angles).some((v) => v % 90 !== 0);

  return (
    <>
      {!file ? (
        <>
          <FileDropZone gateAtDownload acceptDamagedPdf accept=".pdf" onFiles={handleFiles} label="Drop a PDF to rotate, or click to browse" description="Preview and rotate individual pages, all at once, or to a custom angle" />
          {fileError && <p role="alert" className="text-sm text-destructive mt-2">{fileError}</p>}
        </>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-lg border bg-card flex-wrap gap-2">
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{file.name}</p>
              <p className="text-xs text-muted-foreground">{plural(pageCount, "page")} · {formatFileSize(file.size)}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {pageCount > 1 && (
                <>
                  <Button variant="outline" size="sm" onClick={() => turnParity("odd")}>Rotate odd pages</Button>
                  <Button variant="outline" size="sm" onClick={() => turnParity("even")}>Rotate even pages</Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={() => turnAll(-90)} className="gap-1.5" aria-label="Rotate all pages 90° counter-clockwise">
                <RotateCcw className="h-3.5 w-3.5" /> All 90° left
              </Button>
              <Button variant="outline" size="sm" onClick={() => turnAll(90)} className="gap-1.5" aria-label="Rotate all pages 90° clockwise">
                <RotateCw className="h-3.5 w-3.5" /> All 90° right
              </Button>
            </div>
          </div>

          {checking && <p className="text-xs text-muted-foreground" role="status">Checking which pages look sideways...</p>}
          {!checking && Object.keys(suggestions).length > 0 && (
            <Card className="p-3 border-amber-500/40 bg-amber-500/5" data-testid="sideways-banner">
              <div className="flex flex-wrap items-center gap-3">
                <ScanLine className="h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-sm flex-1 min-w-[12rem]">
                  {pendingFixes.length > 0
                    ? <>{plural(pendingFixes.length, "page")} look{pendingFixes.length === 1 ? "s" : ""} sideways or upside down (page {pendingFixes.join(", ")}). Based on the text layer, checked on your device. Nothing is turned until you accept.</>
                    : "The suggested turns are set. Review the previews, then apply."}
                </p>
                {pendingFixes.length > 0 && (
                  <Button size="sm" onClick={() => accept(pendingFixes)} data-testid="fix-all-orientation">Fix {plural(pendingFixes.length, "page")}</Button>
                )}
              </div>
            </Card>
          )}

          {loading ? (
            <PdfThumbnailLoading />
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {thumbnails.map((thumb) => {
                const angle = angles[thumb.pageNumber] ?? 0;
                return (
                  <div key={thumb.pageNumber} className="flex flex-col items-center gap-1.5">
                    <RotatedThumb
                      url={thumb.url}
                      pageNumber={thumb.pageNumber}
                      angle={angle}
                      aspect={aspects[thumb.pageNumber] ?? 0.75}
                      onAspect={(a) => setAspects((prev) => (Math.abs((prev[thumb.pageNumber] ?? 0) - a) < 0.001 ? prev : { ...prev, [thumb.pageNumber]: a }))}
                    />
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => turn(thumb.pageNumber, -90)} title="Rotate this page 90° counter-clockwise" aria-label={`Rotate page ${thumb.pageNumber} 90° counter-clockwise (${fmtDelta(angle)} from current)`}>
                        <RotateCcw className="h-3 w-3" />
                      </Button>
                      <span className="text-xs text-muted-foreground">Page {thumb.pageNumber}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => turn(thumb.pageNumber, 90)} title="Rotate this page 90° clockwise" aria-label={`Rotate page ${thumb.pageNumber} 90° clockwise (${fmtDelta(angle)} from current)`}>
                        <RotateCw className="h-3 w-3" />
                      </Button>
                    </div>
                    {suggestions[thumb.pageNumber] && angle !== suggestions[thumb.pageNumber].turn && (
                      <Button variant="outline" size="sm" className="h-6 px-2 text-[11px] border-amber-500/50" onClick={() => accept([thumb.pageNumber])} data-testid={`fix-page-${thumb.pageNumber}`}>
                        Looks sideways: turn {TURN_LABEL(suggestions[thumb.pageNumber].turn)}
                      </Button>
                    )}
                    <p className="text-[11px] text-muted-foreground" data-testid={`delta-${thumb.pageNumber}`}>{fmtDelta(angle)} from current</p>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => turn(thumb.pageNumber, -0.1)} aria-label={`Turn page ${thumb.pageNumber} 0.1° counter-clockwise`} title="Nudge 0.1° left">
                        <Minus className="h-3 w-3" />
                      </Button>
                      <Input
                        type="number"
                        min={-180}
                        max={180}
                        step={0.1}
                        value={drafts[thumb.pageNumber] ?? String(angle)}
                        onChange={(e) => typeAngle(thumb.pageNumber, e.target.value)}
                        onBlur={() => finishTyping(thumb.pageNumber)}
                        className="h-6 w-16 text-xs px-1.5"
                        title="Angle in degrees (clockwise is positive)"
                        aria-label={`Extra turn for page ${thumb.pageNumber}, in degrees from its current rotation`}
                      />
                      <span className="text-xs text-muted-foreground">°</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => turn(thumb.pageNumber, 0.1)} aria-label={`Turn page ${thumb.pageNumber} 0.1° clockwise`} title="Nudge 0.1° right">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {hasCustomAngle && (
            <Card className="p-3 border-border bg-muted/40">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  Pages with a custom angle are redrawn at their original sharpness and their text stays selectable. The page grows slightly to hold the turned content, and links or form fields on those pages are not kept.
                </p>
              </div>
            </Card>
          )}

          <div className="flex gap-3 pt-2 border-t border-border">
            {processing ? (
              <ProcessingState message={progressMsg || "Rotating..."} onStop={() => { stale(); toast.info("Stopped."); }} />
            ) : result ? (
              <>
                <DownloadButton blob={result} fileName={`${stemOfPdfName(file.name)}-rotated.pdf`} />
                <Button variant="outline" onClick={reset}>Reset</Button>
              </>
            ) : (
              <>
                <Button onClick={() => void apply()} disabled={!hasAnyRotation} className="btn-glow">Apply rotation</Button>
                <Button variant="outline" onClick={reset}>Reset</Button>
                <DownloadTrialHint className="self-center" />
              </>
            )}
          </div>
          {result && <PdfBeforeAfter original={file} result={result} pageCountHint={undefined} initialPage={changedPages[0] ?? 1} leftTitle="Original" rightTitle="Rotated" />}
        </div>
      )}
    </>
  );
}
